import { z } from 'zod';

import { OptionalOfficialDigitCodeSchema } from '../identity/geo-catalog.schemas.js';

export const ADDRESS_KINDS = [
  'RESIDENTIAL',
  'COMMERCIAL',
  'BILLING',
  'SHIPPING',
  'OPERATIONAL',
  'FISCAL',
  'WAREHOUSE',
  'OTHER',
] as const;

export const AddressKindSchema = z.enum(ADDRESS_KINDS);

export type AddressKind = z.infer<typeof AddressKindSchema>;

export const PartyAddressResponseSchema = z.object({
  id: z.uuid(),
  partyId: z.uuid(),
  countryId: z.uuid(),
  stateId: z.uuid().nullable(),
  cityId: z.uuid().nullable(),
  stateText: z.string().nullable(),
  cityText: z.string().nullable(),
  zipCode: z.string().nullable(),
  street: z.string(),
  number: z.string().nullable(),
  complement: z.string().nullable(),
  neighborhood: z.string().nullable(),
  reference: z.string().nullable(),
  municipalCode: z.string().nullable().optional(),
  ibgeCode: z.string().nullable().optional(),
  stateCode: z.string().nullable().optional(),
  kind: AddressKindSchema,
  isPrimary: z.boolean(),
  active: z.boolean(),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
});

export type PartyAddressResponse = z.infer<typeof PartyAddressResponseSchema>;

export const CreatePartyAddressBodySchema = z.object({
  countryCode: z.string().length(2).default('BR'),
  stateId: z.uuid().optional(),
  cityId: z.uuid().optional(),
  stateText: z.string().trim().optional(),
  cityText: z.string().trim().optional(),
  zipCode: z.string().trim().optional(),
  street: z.string().trim().min(1),
  number: z.string().trim().optional(),
  complement: z.string().trim().optional(),
  neighborhood: z.string().trim().optional(),
  reference: z.string().trim().optional(),
  kind: AddressKindSchema.optional(),
  isPrimary: z.boolean().optional(),
  enrichFromExternal: z.boolean().optional(),
  municipalCode: OptionalOfficialDigitCodeSchema,
  ibgeCode: OptionalOfficialDigitCodeSchema,
});

export type CreatePartyAddressBody = z.infer<typeof CreatePartyAddressBodySchema>;

export const UpdatePartyAddressBodySchema = z.object({
  countryCode: z.string().length(2).optional(),
  stateId: z.uuid().nullable().optional(),
  cityId: z.uuid().nullable().optional(),
  stateText: z.string().trim().nullable().optional(),
  cityText: z.string().trim().nullable().optional(),
  zipCode: z.string().trim().nullable().optional(),
  street: z.string().trim().min(1).optional(),
  number: z.string().trim().nullable().optional(),
  complement: z.string().trim().nullable().optional(),
  neighborhood: z.string().trim().nullable().optional(),
  reference: z.string().trim().nullable().optional(),
  kind: AddressKindSchema.optional(),
  isPrimary: z.boolean().optional(),
  municipalCode: OptionalOfficialDigitCodeSchema,
  ibgeCode: OptionalOfficialDigitCodeSchema,
});

export type UpdatePartyAddressBody = z.infer<typeof UpdatePartyAddressBodySchema>;

export const DeletePartyChildBodySchema = z.object({
  deleteReason: z.string().trim().nullable().optional(),
});

export type DeletePartyChildBody = z.infer<typeof DeletePartyChildBodySchema>;

export const CreatePartyAddressResponseSchema = z.object({
  addressId: z.uuid(),
});

export type CreatePartyAddressResponse = z.infer<typeof CreatePartyAddressResponseSchema>;

export const UpdatePartyAddressResponseSchema = z.object({
  addressId: z.uuid(),
  changedFields: z.array(z.string()),
});

export type UpdatePartyAddressResponse = z.infer<typeof UpdatePartyAddressResponseSchema>;

export const DeletePartyAddressResponseSchema = z.object({
  addressId: z.uuid(),
});

export type DeletePartyAddressResponse = z.infer<typeof DeletePartyAddressResponseSchema>;

export const ListPartyAddressesResponseSchema = z.object({
  items: z.array(PartyAddressResponseSchema),
});

export type ListPartyAddressesResponse = z.infer<typeof ListPartyAddressesResponseSchema>;

export const GetPartyAddressByIdResponseSchema = z.object({
  address: PartyAddressResponseSchema,
});

export type GetPartyAddressByIdResponse = z.infer<typeof GetPartyAddressByIdResponseSchema>;

export const PartyAddressParamsSchema = z.object({
  partyId: z.uuid(),
  addressId: z.uuid(),
});

export type PartyAddressParams = z.infer<typeof PartyAddressParamsSchema>;

export const PartyIdParamsSchema = z.object({
  partyId: z.uuid(),
});

export type PartyIdParams = z.infer<typeof PartyIdParamsSchema>;
